import type {
  CoPilotSessionExportArtifact,
  CoPilotSessionExportFormat,
} from '@terra/shared-dto-chat-types';
import {
  sanitizeCoPilotExportMarkdownPlainText,
  sanitizeCoPilotExportAttachmentFilename,
  sanitizeCoPilotExportRequestSlug,
  sanitizeCoPilotExportText,
} from './co-pilot-export-sanitize';

const FORMAT_LABELS: Record<CoPilotSessionExportFormat, string> = {
  markdown: 'Document · Markdown',
  csv: 'Table · CSV',
  har: 'Table · HAR',
};

const FALLBACK_TITLES: Record<CoPilotSessionExportFormat, string> = {
  markdown: 'Export Markdown',
  csv: 'Export CSV',
  har: 'Export HAR',
};

const MAX_PAYLOAD_PARSE_DEPTH = 8;

export function resolveCoPilotSessionExportFormatLabel(
  format: CoPilotSessionExportFormat,
): string {
  return FORMAT_LABELS[format];
}

export function resolveCoPilotSessionExportFallbackTitle(
  format: CoPilotSessionExportFormat,
): string {
  return FALLBACK_TITLES[format];
}

export function buildCoPilotSessionRequestExportFilename({
  requestTitle,
  threadId,
  extension,
}: {
  requestTitle: string;
  threadId: string;
  extension: string;
}): string {
  const slug = sanitizeCoPilotExportRequestSlug(requestTitle);
  const shortId = threadId.replace(/-/g, '').slice(0, 8);
  const normalizedExtension = extension.replace(/^\./, '');
  return `${slug}-${shortId}.${normalizedExtension}`;
}

export function buildCoPilotSessionExportArtifact({
  format,
  filename,
  title,
  applicationId,
  threadId,
  sessionStatus,
  requestFilename,
  csvObjectKinds,
  csvSources,
  csvKind,
  markdownKind,
}: {
  format: CoPilotSessionExportFormat;
  filename: string;
  title: string;
  applicationId: string;
  threadId: string;
  sessionStatus?: CoPilotSessionExportArtifact['sessionStatus'];
  requestFilename?: string;
  csvObjectKinds?: CoPilotSessionExportArtifact['csvObjectKinds'];
  csvSources?: CoPilotSessionExportArtifact['csvSources'];
  csvKind?: CoPilotSessionExportArtifact['csvKind'];
  markdownKind?: CoPilotSessionExportArtifact['markdownKind'];
}): CoPilotSessionExportArtifact {
  return {
    type: 'copilot_session_export',
    format,
    filename: sanitizeCoPilotExportAttachmentFilename(filename),
    title: sanitizeCoPilotExportMarkdownPlainText(
      title.trim() || resolveCoPilotSessionExportFallbackTitle(format),
    ),
    formatLabel: resolveCoPilotSessionExportFormatLabel(format),
    applicationId,
    threadId,
    sessionStatus,
    ...(requestFilename?.trim()
      ? {
          requestFilename: sanitizeCoPilotExportText(requestFilename.trim()),
        }
      : {}),
    ...(csvObjectKinds && csvObjectKinds.length > 0 ? { csvObjectKinds } : {}),
    ...(csvSources && csvSources.length > 0 ? { csvSources } : {}),
    ...(csvKind ? { csvKind } : {}),
    ...(markdownKind ? { markdownKind } : {}),
  };
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function isCoPilotSessionExportFormat(
  value: unknown,
): value is CoPilotSessionExportFormat {
  return value === 'markdown' || value === 'csv' || value === 'har';
}

function toCoPilotSessionExportArtifact(
  record: Record<string, unknown>,
): CoPilotSessionExportArtifact | null {
  if (record.type !== 'copilot_session_export') {
    return null;
  }
  if (!isCoPilotSessionExportFormat(record.format)) {
    return null;
  }
  if (
    typeof record.filename !== 'string' ||
    typeof record.title !== 'string' ||
    typeof record.formatLabel !== 'string' ||
    typeof record.applicationId !== 'string' ||
    typeof record.threadId !== 'string'
  ) {
    return null;
  }

  return {
    type: 'copilot_session_export',
    format: record.format,
    filename: sanitizeCoPilotExportAttachmentFilename(record.filename),
    title: sanitizeCoPilotExportMarkdownPlainText(record.title),
    formatLabel: sanitizeCoPilotExportMarkdownPlainText(record.formatLabel),
    applicationId: record.applicationId,
    threadId: record.threadId,
    sessionStatus:
      record.sessionStatus === 'running' ||
      record.sessionStatus === 'completed' ||
      record.sessionStatus === 'error' ||
      record.sessionStatus === 'aborted'
        ? record.sessionStatus
        : undefined,
    requestFilename:
      typeof record.requestFilename === 'string' &&
      record.requestFilename.trim()
        ? sanitizeCoPilotExportText(record.requestFilename.trim())
        : undefined,
    csvObjectKinds: readCsvObjectKinds(record.csvObjectKinds),
    csvSources: readCsvSources(record.csvSources),
    csvKind: readCsvKind(record.csvKind),
    markdownKind: readMarkdownKind(record.markdownKind),
  };
}

function readMarkdownKind(
  value: unknown,
): CoPilotSessionExportArtifact['markdownKind'] {
  return value === 'transcript' ||
    value === 'objects' ||
    value === 'session' ||
    value === 'http_exchanges'
    ? value
    : undefined;
}

function readCsvKind(value: unknown): CoPilotSessionExportArtifact['csvKind'] {
  return value === 'transcript' ||
    value === 'objects' ||
    value === 'http_exchanges'
    ? value
    : undefined;
}

function readStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  const items = value.filter(
    (item): item is string => typeof item === 'string',
  );
  return items.length > 0 ? items : undefined;
}

function readCsvObjectKinds(
  value: unknown,
): CoPilotSessionExportArtifact['csvObjectKinds'] {
  const items = readStringArray(value);
  if (!items) {
    return undefined;
  }
  const allowed = new Set([
    'Finding',
    'Signal',
    'EndPoint',
    'Trace',
    'WebPage',
    'Skill',
  ]);
  const filtered = items.filter(
    (
      item,
    ): item is NonNullable<
      CoPilotSessionExportArtifact['csvObjectKinds']
    >[number] => allowed.has(item),
  );
  return filtered.length > 0 ? filtered : undefined;
}

function readCsvSources(
  value: unknown,
): CoPilotSessionExportArtifact['csvSources'] {
  const items = readStringArray(value);
  if (!items) {
    return undefined;
  }
  const allowed = new Set(['mentioned', 'created', 'surfaced']);
  const filtered = items.filter(
    (
      item,
    ): item is NonNullable<
      CoPilotSessionExportArtifact['csvSources']
    >[number] => allowed.has(item),
  );
  return filtered.length > 0 ? filtered : undefined;
}

function readPayload(
  value: unknown,
  depth = 0,
): CoPilotSessionExportArtifact | null {
  if (depth > MAX_PAYLOAD_PARSE_DEPTH) {
    return null;
  }

  const parsed = typeof value === 'string' ? safeJsonParse(value) : value;
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }

  const record = parsed as Record<string, unknown>;
  const direct = toCoPilotSessionExportArtifact(record);
  if (direct) {
    return direct;
  }

  const nested = record.output ?? record.result ?? record.content;
  if (nested && nested !== parsed) {
    return readPayload(nested, depth + 1);
  }

  return null;
}

export function readCoPilotSessionExportArtifact(
  output: unknown,
  input: unknown,
): CoPilotSessionExportArtifact | null {
  return readPayload(output) ?? readPayload(input);
}
